/**
 * ConflictChoices — the existing rule a proposal clashes with, and how to fit the two together:
 * Keep both, Replace and Narrow one of them (specification BR-12). Detection is advisory; the
 * landlord decides. Stacked full-width buttons, 56px tall on a phone.
 */

import { Button } from '../../ui/button';
import type {
  ImprovementConflictView,
  ImprovementProposalView,
} from '../../../api/improvement-conversations.api';

export type ConflictChoice = ImprovementConflictView['choices'][number];

interface ChoiceCopy {
  title: string;
  hint: string;
}

/** The Replace label names its reach when a property proposal would replace an all-properties rule. */
function choiceCopy(choice: ConflictChoice, proposal: ImprovementProposalView, conflict: ImprovementConflictView): ChoiceCopy {
  switch (choice) {
    case 'keep_both':
      return {
        title: 'Keep both',
        hint:
          proposal.scope.kind === 'property' && conflict.item.scope.kind === 'landlord'
            ? 'The new rule applies at that property. Your other properties keep your rule.'
            : 'Both stay in force, in the order your playbook sets.',
      };
    case 'replace':
      return conflict.item.scope.kind === 'landlord' && proposal.scope.kind === 'property'
        ? { title: 'Replace my rule everywhere', hint: 'Use the new rule instead of your existing one at all your properties.' }
        : { title: 'Replace it', hint: 'Use the new rule instead of the existing one.' };
    case 'narrow':
      return { title: 'Narrow one of them', hint: 'Tell me what should be different.' };
  }
}

const ORDER: ConflictChoice[] = ['keep_both', 'replace', 'narrow'];

export interface ConflictChoicesProps {
  proposal: ImprovementProposalView;
  conflict: ImprovementConflictView;
  disabled: boolean;
  onChoose: (choice: ConflictChoice) => void;
}

export function ConflictChoices({ proposal, conflict, disabled, onChoose }: ConflictChoicesProps) {
  const choices = ORDER.filter((c) => conflict.choices.includes(c));
  return (
    <div className="flex flex-col gap-2.5" data-testid="improvement-conflict">
      <p className="m-0 text-sm leading-normal text-foreground">
        That clashes with {conflict.item.scope.kind === 'landlord' ? 'a rule you already have for all your properties' : 'a rule you already have'}:
      </p>
      <blockquote
        className="m-0 rounded-lg border border-border px-3 py-2.5 text-sm text-foreground"
        data-testid="improvement-conflict-existing"
      >
        “{conflict.item.text}”
      </blockquote>
      <p className="m-0 text-sm leading-normal text-foreground">How should they fit together?</p>
      <div role="group" aria-label="Resolve the clash" className="flex flex-col gap-2">
        {choices.map((choice) => {
          const copy = choiceCopy(choice, proposal, conflict);
          return (
            <Button
              key={choice}
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={() => onChoose(choice)}
              data-testid={`improvement-conflict-${choice}`}
              className={`h-auto min-h-[56px] w-full flex-col items-start gap-0.5 whitespace-normal rounded-[10px] px-3.5 py-2.5 text-left ${
                choice === 'keep_both' ? 'border-primary' : ''
              }`}
            >
              <span className="text-sm font-semibold text-foreground">{copy.title}</span>
              <span className="text-xs font-normal text-muted-foreground">{copy.hint}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export default ConflictChoices;
